import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';
import { getDirections } from '../../../services/directionsService';

// Create vehicle icon SVG
const createVehicleIcon = (color = '#1F2937') => {
  const svgIcon = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <g>
        <!-- Vehicle shadow -->
        <ellipse cx="20" cy="35" rx="16" ry="3" fill="#000000" opacity="0.2"/>
        <!-- Vehicle body -->
        <rect x="8" y="15" width="24" height="15" rx="3" fill="${color}" stroke="#FFFFFF" stroke-width="2"/>
        <!-- Vehicle front -->
        <rect x="10" y="10" width="20" height="8" rx="4" fill="${color}" stroke="#FFFFFF" stroke-width="2"/>
        <!-- Wheels -->
        <circle cx="13" cy="30" r="4" fill="#374151" stroke="#FFFFFF" stroke-width="2"/>
        <circle cx="27" cy="30" r="4" fill="#374151" stroke="#FFFFFF" stroke-width="2"/>
        <!-- Windows -->
        <rect x="12" y="12" width="16" height="6" rx="2" fill="#E5E7EB" stroke="#D1D5DB" stroke-width="1"/>
        <!-- Windshield -->
        <rect x="14" y="14" width="12" height="3" rx="1" fill="#F3F4F6"/>
        <!-- Headlights -->
        <circle cx="11" cy="14" r="1.5" fill="#FEF3C7" stroke="#F59E0B" stroke-width="0.5"/>
        <circle cx="29" cy="14" r="1.5" fill="#FEF3C7" stroke="#F59E0B" stroke-width="0.5"/>
        <!-- Direction indicator (small arrow) -->
        <polygon points="20,5 23,10 17,10" fill="#3B82F6" stroke="#FFFFFF" stroke-width="1"/>
      </g>
    </svg>
  `)}`;
  
  if (!window.google?.maps) return { url: svgIcon };
  return {
    url: svgIcon,
    scaledSize: new window.google.maps.Size(40, 40),
    anchor: new window.google.maps.Point(20, 20)
  };
};

const mapOptions = {
  disableDefaultUI: true,
  clickableIcons: false,
  keyboardShortcuts: false,
  gestureHandling: 'greedy',   // mobile-friendly gestures

  styles: [
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  ],
};

// Generate consistent random color for each stop based on its ID
const generateStopColor = (stopId, index) => {
  if (!stopId) return '#6B7280'; // Default gray
  
  // Create a hash from the stopId
  let hash = 0;
  const idString = String(stopId);
  for (let i = 0; i < idString.length; i++) {
    hash = idString.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate HSL color with good saturation and lightness for visibility
  const hue = Math.abs(hash) % 360;
  const saturation = 65 + (Math.abs(hash) % 20); // 65-85%
  const lightness = 45 + (Math.abs(hash) % 15); // 45-60%
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

// Helper function to get stop color based on status
const getStopMarkerColor = (job, index, currentWaypointId) => {

  // Use consistent random color for this stop
  return generateStopColor(job.Id || job.stopId, index);
};

// Generate consistent color for a vehicle based on its ID
const generateVehicleColor = (vehicleId) => {
  if (!vehicleId) return '#6B7280'; // Default gray
  
  // Create a hash from the vehicleId
  let hash = 0;
  const idString = String(vehicleId);
  for (let i = 0; i < idString.length; i++) {
    hash = idString.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate HSL color with good saturation and lightness for visibility
  const hue = Math.abs(hash) % 360;
  const saturation = 70 + (Math.abs(hash) % 20); // 70-90%
  const lightness = 50 + (Math.abs(hash) % 20); // 50-70%
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};



const FixedWaypointMap = ({ 
  googleApiKey, 
  isPhone,
  center, 
  jobs = [], 
  vehiclePosition, 
  currentWaypoint,
  expectedTime,
  onDirectionsCalculated,
  isAccurate,
  handleRequestDirections,
  allStops = [],
  isFixedRoute = true,
  onLiveETAsUpdate,
  onMapMoved,
  currentScheduleStops = [],
  scheduleStatus = 'active',
  onMapLoad,
  fixedRouteData = null,
  groupVehicles = new Map(),
  appLocation = null, // Current GPS location
  routeDetails = null, // Route details including start/end coordinates
  showPolylines = true, // Control whether to show polylines/directions
  fleetSettings,
}) => {
  const [map, setMap] = useState(null);
  const [directionsPath, setDirectionsPath] = useState([]);
  const [isCalculatingDirections, setIsCalculatingDirections] = useState(false);
  const [lastDirectionsTime, setLastDirectionsTime] = useState(null);
  const [lastValidState, setLastValidState] = useState(null);
  const [waypoints, setWaypoints] = useState([]);
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef(null);

  // Stable polyline options
  const routePolylineOptions = useMemo(() => ({
    strokeColor: '#3B82F6',
    strokeOpacity: 0.8,
    strokeWeight: 4,
  }), []);

  const fallbackPolylineOptions = useMemo(() => ({
    strokeColor: '#3B82F6',
    strokeOpacity: 0.6,
    strokeWeight: 3,
    clickable: false,
    draggable: false,
    editable: false,
    visible: true,
    zIndex: 1,
  }), []);

  // Get current position for directions (prefer appLocation over vehiclePosition)
  const getCurrentPosition = useCallback(() => {
    return appLocation || vehiclePosition || center;
  }, [appLocation, vehiclePosition, center]);

  // Get upcoming stops including current waypoint + next 3
  const getUpcomingStopsForDirections = useCallback(() => {
    if (!currentWaypoint) {
      return [];
    }

    let upcomingStops = [];

    // Try to get from currentScheduleStops first
    if (currentScheduleStops && currentScheduleStops.length > 0) {
      const currentIndex = currentScheduleStops.findIndex(stop => stop.Id === currentWaypoint.Id);
      if (currentIndex !== -1) {
        // Get current + next 3 stops
        upcomingStops = currentScheduleStops.slice(currentIndex, currentIndex + 4);
      } else {
        // Fallback: first 4 stops
        upcomingStops = currentScheduleStops.slice(0, 4);
      }
    }

    // Final fallback to allStops
    if (upcomingStops.length === 0 && allStops && allStops.length > 0) {
      upcomingStops = allStops.slice(0, 4);
    }

    return upcomingStops.filter(stop => 
      stop && stop.Lat && stop.Lon && 
      !isNaN(parseFloat(stop.Lat)) && !isNaN(parseFloat(stop.Lon))
    );
  }, [currentWaypoint, currentScheduleStops, allStops]);

  // Check if we should calculate new directions (throttling)
  const shouldCalculateDirections = useCallback(() => {
    const now = Date.now();
    const twoMinutes = 2 * 60 * 1000; // 2 minutes in milliseconds
    
    // Don't calculate if we don't have required components or already calculating
    const currentPosition = getCurrentPosition();
    const upcomingStops = getUpcomingStopsForDirections();
    
    if (!map || !currentPosition || !currentWaypoint || 
        upcomingStops.length === 0 || isCalculatingDirections) {
      return false;
    }

    // Always calculate if this is the first time
    if (!lastDirectionsTime) {
      console.log('First time calculating directions');
      return true;
    }
    
    // Calculate if enough time has passed OR if waypoint changed
    const waypointChanged = !lastValidState || lastValidState.waypointId !== currentWaypoint.Id;
    const timeThrottlePassed = (now - lastDirectionsTime) >= twoMinutes;
    
    console.log('Direction calculation decision:', {
      waypointChanged,
      timeThrottlePassed,
      timeSinceLastCalculation: now - lastDirectionsTime
    });
    
    return waypointChanged || timeThrottlePassed;
  }, [map, getCurrentPosition, getUpcomingStopsForDirections, currentWaypoint, lastDirectionsTime, lastValidState, isCalculatingDirections]);

  // Main directions calculation function using OSRM/Google service
  const calculateDirections = useCallback(async () => {
    console.log('calculateDirections called, checking conditions...');
    
    if (!showPolylines || !shouldCalculateDirections()) {
      console.log('calculateDirections skipped:', { showPolylines, shouldCalculate: shouldCalculateDirections() });
      return;
    }

    const currentPosition = getCurrentPosition();
    const upcomingStops = getUpcomingStopsForDirections();

    if (!currentPosition || !currentWaypoint || upcomingStops.length === 0 || !map) {
      console.log('calculateDirections aborted due to missing requirements');
      return;
    }

    setIsCalculatingDirections(true);
    
    try {
      const origin = { 
        lat: parseFloat(currentPosition.lat), 
        lng: parseFloat(currentPosition.lng) 
      };
      
      const intermediateWaypoints = upcomingStops.slice(0, -1).map(stop => ({
        lat: parseFloat(stop.Lat || stop.Latitude), 
        lng: parseFloat(stop.Lon || stop.Longitude) 
      }));

      const destination = {
        lat: parseFloat(upcomingStops[upcomingStops.length - 1].Lat || upcomingStops[upcomingStops.length - 1].Latitude),
        lng: parseFloat(upcomingStops[upcomingStops.length - 1].Lon || upcomingStops[upcomingStops.length - 1].Longitude)
      };

      const osrmUrl = fleetSettings?.osrmUrl || null;
      const result = await getDirections({ origin, destination, waypoints: intermediateWaypoints, osrmUrl });

      setIsCalculatingDirections(false);

      if (result && result.path.length > 0) {
        setDirectionsPath(result.path);
        setLastDirectionsTime(Date.now());
        setLastValidState({
          waypointId: currentWaypoint.Id,
          position: currentPosition,
          stopsCount: upcomingStops.length
        });
        console.log('Directions calculated successfully via', result.source, 'for', upcomingStops.length, 'stops');
      } else {
        setDirectionsPath([]);
      }
    } catch (error) {
      console.error('Error calculating directions:', error);
      setIsCalculatingDirections(false);
      setDirectionsPath([]);
    }
  }, [showPolylines, shouldCalculateDirections, getCurrentPosition, getUpcomingStopsForDirections, currentWaypoint, map, fleetSettings?.osrmUrl]);

  // Main effect to trigger directions calculation when map and dependencies are ready
  useEffect(() => {
    if (map && mapReady) {
      const timeoutId = setTimeout(() => {
        calculateDirections();
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, [map, mapReady, currentWaypoint, appLocation, vehiclePosition, calculateDirections]);

  // Set waypoints for markers when schedule stops change
  useEffect(() => {
    const upcomingStops = getUpcomingStopsForDirections();
    setWaypoints(upcomingStops);
  }, [getUpcomingStopsForDirections]);



  const onLoad = useCallback((loadedMap) => {
    console.log('FixedWaypointMap: Map loaded');
    setMap(loadedMap);
    mapRef.current = loadedMap;
    
    // Force map resize after load (especially important for mobile)
    setTimeout(() => {
      if (loadedMap && window.google?.maps) {
        window.google.maps.event.trigger(loadedMap, 'resize');
        loadedMap.setCenter(center);
      }
      
      // Set map as ready after ensuring it's fully loaded
      console.log('FixedWaypointMap: Setting map as ready');
      setMapReady(true);
    }, 200);
    
    // Call parent's onMapLoad if provided
    if (onMapLoad) {
      onMapLoad(loadedMap);
    }
  }, [onMapLoad, center]);

  const onUnmount = useCallback(() => {
    setMap(null);
    mapRef.current = null;
  }, []);

  // Create polyline path from current position through upcoming stops (fallback)
  const createFallbackPolylinePath = useCallback(() => {
    const currentPosition = getCurrentPosition();
    const upcomingStops = getUpcomingStopsForDirections();
    
    if (!currentPosition || upcomingStops.length === 0) {
      return [];
    }

    // Start with current position, then add upcoming stops
    const path = [
      { lat: parseFloat(currentPosition.lat), lng: parseFloat(currentPosition.lng) },
      ...upcomingStops.map(stop => ({
        lat: parseFloat(stop.Lat || stop.Latitude),
        lng: parseFloat(stop.Lon || stop.Longitude)
      }))
    ];

    return path;
  }, [getCurrentPosition, getUpcomingStopsForDirections]);

  // Get the fallback polyline path
  const fallbackPolylinePath = createFallbackPolylinePath();

  // Debug logging for vehicle position
  useEffect(() => {
    console.log('FixedWaypointMap Debug - Vehicle Position:', {
      vehiclePosition,
      center,
      appLocation,
      hasVehiclePosition: !!vehiclePosition,
      vehiclePositionCoords: vehiclePosition ? `${vehiclePosition.lat}, ${vehiclePosition.lng}` : 'none'
    });
  }, [vehiclePosition, center, appLocation]);

  // Get markers for rendering

const mapStyles = [
    {
      elementType: 'geometry',
      stylers: [{ color: '#f5f5f5' }],
    },
    {
      elementType: 'labels.icon',
      stylers: [{ visibility: 'off' }],
    },
    {
      elementType: 'labels.text.fill',
      stylers: [{ color: '#616161' }],
    },

  ];
  return (
    <div className="relative w-full border rounded-lg shadow-sm" 
         style={{ 
           backgroundColor: '#f0f0f0', 
           height: isPhone ? '500px' : '500px', // Fixed pixel heights for Google Maps
           width: '100%'
         }}>

      
      <GoogleMap
        mapContainerStyle={{
          width: '100%',
          height: isPhone ? '500px' : '500px', // Fixed height for mobile, flexible for desktop
        }}
        center={center}
        zoom={isPhone ? 14 : 15}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={mapOptions}
        styles={mapStyles}
      >
        {/* Route polyline from directions service (OSRM or Google) */}
        {showPolylines && directionsPath.length > 1 && (
          <Polyline
            path={directionsPath}
            options={routePolylineOptions}
          />
        )}

        {/* Fallback straight-line polyline when directions unavailable */}
        {showPolylines && directionsPath.length === 0 && fallbackPolylinePath.length > 1 && (
          <Polyline
            path={fallbackPolylinePath}
            options={fallbackPolylineOptions}
          />
        )}

        {/* Vehicle Position Marker - only render when map is ready */}
        {mapReady && vehiclePosition && (
          <Marker
            position={vehiclePosition}
            icon={createVehicleIcon('#2563EB')}
            title="Shuttle Vehicle"
            zIndex={1000}
          />
        )}

        {/* Current Location Marker - only render when map is ready */}
        {mapReady && center && window.google?.maps && (
          <Marker
            position={center}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 6,
              fillColor: isAccurate ? '#3B82F6' : '#EF4444',
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 2,
            }}
            title="Your Location"
            zIndex={999}
          />
        )}

        {/* Stop markers - using consistent random colors - only render when map is ready */}
        {mapReady && waypoints && Array.isArray(waypoints) && waypoints.length > 0 && window.google?.maps?.SymbolPath && waypoints.map((stop, index) => {
          const stopColor = getStopMarkerColor(stop, index);
          const isCurrent = stop.Id === currentWaypoint?.Id;

          return (
            <Marker
              key={`fixed-stop-${stop.Id}_${index}`}
              position={{ lat: parseFloat(stop.Lat), lng: parseFloat(stop.Lon) }}
              title={`${stop.Name}`}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale:  10,
                fillColor: stopColor,
                fillOpacity: 1,
                strokeColor: isCurrent ? '#1E40AF' : '#FFFFFF',
                strokeWeight: isCurrent ? 4 : 3,
              }}
              zIndex={isCurrent ? 1000 : 900}
              visible={true}
            />
          );
        })}

        {/* Group Vehicle markers - show other vehicles in the same route group - only render when map is ready */}
        {mapReady && groupVehicles && Array.from(groupVehicles.values()).map((vehicle) => {
          const vehicleColor = generateVehicleColor(vehicle.vehicleId);
          const isStale = new Date() - new Date(vehicle.timestamp) > 5 * 60 * 1000; // 5 minutes stale threshold
          
          return (
            <Marker
              key={`group-vehicle-${vehicle.vehicleId}`}
              position={{ lat: vehicle.latitude, lng: vehicle.longitude }}
              icon={createVehicleIcon(vehicleColor)}
              title={`Vehicle ${vehicle.vehicleId}${vehicle.routeId ? ` (Route ${vehicle.routeId})` : ''}${isStale ? ' - Last seen ' + new Date(vehicle.timestamp).toLocaleTimeString() : ''}`}
              zIndex={800}
              opacity={isStale ? 0 : 1.0}
            />
          );
        })}

  
      </GoogleMap>
    </div>
  );
};

export default FixedWaypointMap;
export { generateStopColor };
